import { rem } from '@visiba/std';
import { ForwardedRef, forwardRef, InputHTMLAttributes } from 'react';
import styled from 'styled-components';
import { theme } from './input.theme';

type InputTypes =
	| 'date'
	| 'datetime-local'
	| 'email'
	| 'hidden'
	| 'month'
	| 'number'
	| 'password'
	| 'search'
	| 'tel'
	| 'text'
	| 'time'
	| 'url'
	| 'week';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
	/**
	 * The default value used by React. [Read
	 * more](https://reactjs.org/docs/uncontrolled-components.html#default-values).
	 */
	defaultValue?: string | number;

	/**
	 * Use to make the input field disabled. In many scenarios it's preferred to
	 * use `readOnly` instead of `disabled`. E.g. if you want to prevent the user
	 * to input anything while loading/saving you should use `readOnly`.
	 */
	disabled?: boolean;

	/**
	 * Reflects the value of the native HTML Input `id` property.
	 */
	id?: string;

	/**
	 * Use to indicate if the input field has invalid input or not.
	 */
	invalid?: boolean;

	/**
	 * Use to make the input field read only.
	 */
	readOnly?: boolean;

	/**
	 * Use to indicate if the input field is mandatory or not.
	 */
	required?: boolean;
	optional?: boolean;

	/**
	 * Reflects the value of the native HTML Input `type` property.
	 */
	type?: InputTypes;

	/**
	 * Reflects the value of the native HTML Input `value` property.
	 */
	value?: string | number;
}

interface StyledInputProps {
	invalid?: boolean;
}

// @internal
export const elements = {
	container: styled.div`
		position: relative;
		display: flex;
	`,
	input: styled.input<StyledInputProps>`
		all: unset;

		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;

		box-sizing: border-box;
		flex: 1 1 auto;
		border: ${rem(1)} solid ${theme.getValue('baseBorderColor')};
		min-height: ${theme.getValue('baseMinHeight')};
		padding: ${theme.getValue('basePaddingVertical')} ${theme.getValue('basePaddingHorizontal')};
		font: ${theme.getValue('baseFont')};
		color: ${theme.getValue('baseTextColor')};
		border-radius: ${theme.getValue('baseBorderRadius')};
		outline: none;
		transition: border-color 150ms;
		background: ${theme.getValue('baseBackground')};

		&:read-only,
		&:disabled {
			background: ${theme.getValue('readOnlyBackground')};
			border-color: ${theme.getValue('readOnlyBorderColor')};
			opacity: 0.7;
		}

		&:disabled {
			cursor: not-allowed;
		}

		&::placeholder {
			color: ${theme.getValue('placeholderTextColor')};
		}

		&:hover:not(:disabled, :read-only),
		&:focus:not(:disabled, :read-only) {
			border-color: ${theme.getValue('hoverBorderColor')};
		}

		&:focus:not(:disabled, :read-only) {
			outline: 3px solid ${theme.getValue('focusRingColor')};
		}

		&[aria-invalid='true'] {
			border-color: ${theme.getValue('invalidBorderColor')};
		}
	`,
};

function CellulaInput({ invalid, ...rest }: InputProps, ref: ForwardedRef<HTMLInputElement>): JSX.Element {
	return (
		<elements.container>
			<elements.input aria-invalid={invalid || undefined} {...rest} ref={ref} />
		</elements.container>

		/* 		<elements.container>
			{invalid && <elements.invalidIcon type='warningCircle' />}
			<elements.input aria-invalid={invalid || undefined} {...rest} ref={ref} />
		</elements.container> */
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to allow the user to input content.
 */
const Input = forwardRef(CellulaInput);

export type { InputProps, InputTypes };
export { Input };
